import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faIndianRupeeSign,
  faReceipt,
  faPlus,
  faDownload,
  faCalendarWeek,
} from "@fortawesome/free-solid-svg-icons";

const fmt = (n) =>
  (n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const SummaryCard = ({ label, value, prefix, colorClass }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-5 py-4">
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
    <p className={`text-2xl font-bold ${colorClass || "text-gray-800"}`}>
      {prefix}{fmt(value)}
    </p>
  </div>
);

const EMPTY_PAYOUT = {
  amount: "",
  method: "UPI",
  reference: "",
  note: "",
  conversionsCovered: "",
};

const METHODS = ["UPI", "NEFT", "Cash", "Bank Transfer"];

const InfluencerDetail = () => {
  const { promoId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutForm, setPayoutForm] = useState(EMPTY_PAYOUT);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await func.get(`mst-admin/influencer/${promoId}/detail`);
      if (res?.status === 200 && res.data?.success) {
        setData(res.data);
      } else {
        toast.error("Failed to load influencer details");
      }
    } catch {
      toast.error("Failed to load influencer details");
    }
    setLoading(false);
  }, [promoId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePayoutChange = (field, val) => {
    setPayoutForm((p) => ({ ...p, [field]: val }));
  };

  const handlePayoutSubmit = async () => {
    if (!payoutForm.amount || Number(payoutForm.amount) <= 0) {
      toast.warn("Enter a valid amount");
      return;
    }
    setSaving(true);
    try {
      const res = await func.post("mst-admin/influencer/payout", {
        promoCodeId: promoId,
        amount: Number(payoutForm.amount),
        method: payoutForm.method,
        reference: payoutForm.reference || undefined,
        note: payoutForm.note || undefined,
        conversionsCovered: payoutForm.conversionsCovered
          ? Number(payoutForm.conversionsCovered)
          : undefined,
      });
      if (res?.status === 201 && res.data?.success) {
        toast.success("Payout recorded successfully!");
        setShowPayoutModal(false);
        setPayoutForm(EMPTY_PAYOUT);
        fetchData();
      } else {
        toast.error(res?.data?.message || "Failed to record payout");
      }
    } catch {
      toast.error("Failed to record payout");
    }
    setSaving(false);
  };

  const exportCSV = () => {
    if (!data) return;
    const { promo, summary, transactions, payouts } = data;

    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

    const rows = [
      [`MatchMatrix Influencer Report — ${promo.code} (@${promo.influencer || "N/A"})`],
      [`Generated: ${new Date().toLocaleString("en-IN")}`],
      [],
      ["SUMMARY"],
      ["Conversions", summary.conversions],
      ["Revenue (₹)", summary.revenue],
      ["Commission Earned (₹)", summary.commissionOwed],
      ["Commission Paid (₹)", summary.commissionPaid],
      ["Queued for Approval (₹)", summary.scheduledCommission],
      ["Unclaimed (₹)", summary.pendingCommission],
      [],
      ["TRANSACTIONS"],
      ["#", "User", "Plan", "Amount Paid (₹)", "Commission (₹)", "Date"],
      ...transactions.map((t, i) => [
        i + 1,
        escape(t.userName),
        escape(t.planKey || "-"),
        t.amountPaid,
        t.commission,
        escape(fmtDate(t.usedAt)),
      ]),
      [],
      ["PAYOUT HISTORY"],
      ["#", "Amount (₹)", "Method", "Reference", "Note", "Date"],
      ...payouts.map((p, i) => [
        i + 1,
        p.amount,
        escape(p.method),
        escape(p.reference || "-"),
        escape(p.note || "-"),
        escape(fmtDate(p.createdAt)),
      ]),
    ];

    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `influencer_${promo.code}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-6xl mx-auto">
        {/* Back link */}
        <button
          onClick={() => navigate("/influencer-hub")}
          className="flex items-center gap-2 text-white/80 hover:text-white text-sm mb-5 transition"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Influencer Hub
        </button>

        {loading ? (
          <div className="bg-white rounded-xl p-10 text-center text-gray-400 animate-pulse">
            Loading influencer data...
          </div>
        ) : !data ? (
          <div className="bg-white rounded-xl p-10 text-center text-red-500">
            Failed to load data. Please go back and try again.
          </div>
        ) : (
          <>
            {/* Promo Info Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-6 py-5 mb-5 flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <span className="font-mono text-2xl font-bold text-gray-800">{data.promo.code}</span>
                  {data.promo.active !== false ? (
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Active</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-600">Inactive</span>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  <span className="font-medium text-gray-700">{data.promo.influencer || "No handle set"}</span>
                  {" · "}Commission Rate: <span className="font-semibold text-indigo-700">{data.promo.commissionRate}%</span>
                </p>
                {data.promo.publicToken && (
                  <p className="text-xs text-gray-400 mt-0.5 font-mono">
                    Token: {data.promo.publicToken}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={exportCSV}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-lg transition shadow"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Export CSV
                </button>
                <button
                  onClick={() => setShowPayoutModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition shadow"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Record Payout
                </button>
              </div>
            </div>

            {/* Summary Cards */}
            {/* "Pending" is now what is UNCLAIMED — not paid, and not already queued.
                Queued sits beside it so ₹0 pending never reads as nothing owed. */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
              <SummaryCard label="Conversions" value={data.summary.conversions} colorClass="text-indigo-700" />
              <SummaryCard label="Revenue" value={data.summary.revenue} prefix="₹" colorClass="text-green-700" />
              <SummaryCard label="Earned" value={data.summary.commissionOwed} prefix="₹" colorClass="text-orange-600" />
              <SummaryCard label="Paid" value={data.summary.commissionPaid} prefix="₹" colorClass="text-teal-700" />
              <SummaryCard
                label="Queued"
                value={data.summary.scheduledCommission}
                prefix="₹"
                colorClass={data.summary.scheduledCommission > 0 ? "text-amber-600" : "text-gray-400"}
              />
              <SummaryCard
                label="Unclaimed"
                value={data.summary.pendingCommission}
                prefix="₹"
                colorClass={data.summary.pendingCommission > 0 ? "text-red-600" : "text-gray-400"}
              />
            </div>

            {/* Weekly Breakdown */}
            {data.weeklyBreakdown?.length > 0 && (
              <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                  <FontAwesomeIcon icon={faCalendarWeek} className="text-purple-600" />
                  <h2 className="font-semibold text-gray-800 text-sm">Week-by-Week</h2>
                  <span className="ml-auto text-xs text-gray-400">{data.weeklyBreakdown.length} week{data.weeklyBreakdown.length !== 1 ? "s" : ""}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        {["Week", "Conversions", "Revenue", "Earned", "Paid Out", "Status"].map((h) => (
                          <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.weeklyBreakdown.map((w, i) => {
                        const pending = parseFloat((w.commission - w.paidOut).toFixed(2));
                        return (
                          <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 transition">
                            <td className="px-4 py-3 text-gray-700 text-xs whitespace-nowrap">
                              {fmtDate(w.weekStart)} – {fmtDate(w.weekEnd)}
                            </td>
                            <td className="px-4 py-3 text-indigo-700 font-semibold">{w.conversions}</td>
                            <td className="px-4 py-3 text-gray-800 font-semibold">₹{fmt(w.revenue)}</td>
                            <td className="px-4 py-3 text-orange-600 font-semibold">₹{fmt(w.commission)}</td>
                            <td className="px-4 py-3 text-teal-700 font-semibold">₹{fmt(w.paidOut)}</td>
                            <td className="px-4 py-3">
                              {pending <= 0 ? (
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Settled</span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">₹{fmt(pending)} due</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Transactions Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <FontAwesomeIcon icon={faReceipt} className="text-indigo-600" />
                <h2 className="font-semibold text-gray-800 text-sm">Transactions</h2>
                <span className="ml-auto text-xs text-gray-400">{data.transactions.length} record{data.transactions.length !== 1 ? "s" : ""}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {["#", "User", "Plan", "Amount Paid", "Commission", "Date"].map((h) => (
                        <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.transactions.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-10 text-center text-gray-400 text-sm">
                          No transactions yet
                        </td>
                      </tr>
                    ) : (
                      data.transactions.map((t, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 transition">
                          <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                          <td className="px-4 py-3 text-gray-700">{t.userName}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded font-mono">
                              {t.planKey || "-"}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-semibold text-gray-800">₹{fmt(t.amountPaid)}</td>
                          <td className="px-4 py-3 font-semibold text-indigo-600">₹{fmt(t.commission)}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(t.usedAt)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Payouts History Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <FontAwesomeIcon icon={faIndianRupeeSign} className="text-teal-600" />
                <h2 className="font-semibold text-gray-800 text-sm">Payout History</h2>
                <span className="ml-auto text-xs text-gray-400">{data.payouts.length} payout{data.payouts.length !== 1 ? "s" : ""}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {["#", "Amount", "Method", "Reference", "Note", "Date"].map((h) => (
                        <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.payouts.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-10 text-center text-gray-400 text-sm">
                          No payouts recorded yet
                        </td>
                      </tr>
                    ) : (
                      data.payouts.map((p, i) => (
                        <tr key={p._id || i} className="border-b border-gray-100 hover:bg-gray-50 transition">
                          <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                          <td className="px-4 py-3 font-semibold text-teal-700">₹{fmt(p.amount)}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded font-medium">
                              {p.method}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600 font-mono text-xs">{p.reference || "-"}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{p.note || "-"}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(p.createdAt)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Record Payout Modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">Record Payout</h3>
              {data && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Pending: <span className="font-semibold text-red-600">₹{fmt(data.summary.pendingCommission)}</span>
                </p>
              )}
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Amount (₹) *</label>
                <input
                  type="number"
                  min="1"
                  value={payoutForm.amount}
                  onChange={(e) => handlePayoutChange("amount", e.target.value)}
                  placeholder="Enter amount paid"
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Payment Method</label>
                <select
                  value={payoutForm.method}
                  onChange={(e) => handlePayoutChange("method", e.target.value)}
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 transition bg-white"
                >
                  {METHODS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Reference / UTR (optional)</label>
                <input
                  type="text"
                  value={payoutForm.reference}
                  onChange={(e) => handlePayoutChange("reference", e.target.value)}
                  placeholder="Transaction ID or UTR number"
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Note (optional)</label>
                <input
                  type="text"
                  value={payoutForm.note}
                  onChange={(e) => handlePayoutChange("note", e.target.value)}
                  placeholder="Any remarks"
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Conversions Covered (optional)</label>
                <input
                  type="number"
                  min="0"
                  value={payoutForm.conversionsCovered}
                  onChange={(e) => handlePayoutChange("conversionsCovered", e.target.value)}
                  placeholder="Number of conversions this payout covers"
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 transition"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => {
                  setShowPayoutModal(false);
                  setPayoutForm(EMPTY_PAYOUT);
                }}
                className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handlePayoutSubmit}
                disabled={saving}
                className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium disabled:opacity-50"
              >
                {saving ? "Saving..." : "Record Payout"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfluencerDetail;
